import React from "react";
import { authorizedFetch } from "./boot.js";

// Whether this vendor's pages show inspection figures at all.
//
// A vendor who does not use the Inspections component was reading zeroes that
// look like "nobody has inspected your horses". The setting is vendor-wide and
// owner-written, and it is a display choice only: every grant is identical in
// both states, and nothing here may hide a control that logs a visit.
//
// This module performs no registration and holds no state of its own. The flag
// arrives in `/ui/bootstrap@1` and the one copy of it lives on `window.VD`.

const EVENT = "vd:inspection-display-changed";

/** Absent means shown. An older bundle, or a payload from before the setting
 *  existed, must never blank a vendor's data because a field was missing. */
export function inspectionDataShown() {
  return window.VD?.inspections?.showData !== false;
}

export function canSetInspectionData() {
  return window.VD?.inspections?.canSetShowData === true;
}

/** Re-render this screen when the setting changes on another one. */
export function useInspectionData() {
  const [shown, setShown] = React.useState(inspectionDataShown);
  React.useEffect(() => {
    const listen = () => setShown(inspectionDataShown());
    window.addEventListener(EVENT, listen);
    return () => window.removeEventListener(EVENT, listen);
  }, []);
  return shown;
}

export async function setInspectionData(show) {
  const inspections = window.VD?.inspections || {};
  const response = await authorizedFetch("/actions/vendor.inspections.display_preference@1/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      input: {
        sale: inspections.sale || "26R2R",
        show_data: show,
        expected_revision: inspections.showDataRevision || 0,
      },
      idempotency_key: window.crypto.randomUUID(),
    }),
  });
  if (!response.ok) {
    const error = new Error("request_failed");
    error.status = response.status;
    throw error;
  }
  // The server accepted it, so the payload this page booted from is one
  // revision old. Both fields move together: leaving the revision behind would
  // have the next write refused as stale against a value this page itself set.
  window.VD.inspections = {
    ...inspections,
    showData: show,
    showDataRevision: (inspections.showDataRevision || 0) + 1,
  };
  window.dispatchEvent(new CustomEvent(EVENT));
}
